import * as RQ from '@tanstack/react-query';
import {
  createElement,
  type ReactElement,
  type ReactNode,
  useRef,
} from 'react';

import { createAbortError } from './abortError';
import getObjectId from './getObjectId';
import isReadonlyArray from './isReadonlyArray';
import memoizeWithWeakMap from './memoizeWithWeakMap';
import noop from './noop';
import { DEFAULT_REQUEST_CONTEXT, type RequestContext } from './requestContext';
import { unsafe_asMutable } from './types';
import {
  type AnyRequest,
  isQueryKey,
  queryFn,
  type QueryKey,
  type RequestData,
  type Updater,
} from './useQuery';

export { type QueryClient } from '@tanstack/react-query';

const getFunctionId = memoizeWithWeakMap((obj: { name: string }) => {
  const randomSuffix = Math.random().toString(36).slice(7).padEnd(6, '0');
  return `${obj.name}-${randomSuffix}`;
});

function extractRelevantMeta(
  meta: unknown,
): Record<string, string | undefined> {
  const result: Record<string, string | undefined> = {};
  if (typeof meta === 'object' && meta !== null) {
    // Extend this with additional meta properties that should affect query keys
    if ('isSandbox' in meta && typeof meta.isSandbox === 'boolean') {
      result.isSandbox = String(meta.isSandbox);
    }
  }
  return result;
}

function getRequestContextJSON({
  publishingSiteId,
  experience,
  meta,
}: RequestContext): Record<string, string | undefined> {
  return {
    studioExperience: experience?.studioExperience ?? undefined,
    subdivisionTenant: experience?.subdivisionTenant ?? undefined,
    subdivisionMarket: experience?.subdivisionMarket ?? undefined,
    publishingSite: experience?.publishingSite ?? publishingSiteId ?? undefined,
    commerceLine: experience?.commerceLine ?? undefined,
    productLine: experience?.productLine ?? undefined,
    ...extractRelevantMeta(meta),
  };
}

function queryKeyHashFn(queryKey: unknown): string {
  if (isQueryKey(queryKey)) {
    const [request, context, ...params] = queryKey;

    return RQ.hashKey([
      getFunctionId(request),
      getRequestContextJSON(context),
      ...params,
    ]);
  }

  if (isReadonlyArray(queryKey)) {
    return RQ.hashKey(queryKey);
  }

  return '';
}

export function createQueryClient(): RQ.QueryClient {
  return new RQ.QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1 * 60 * 1000,
        gcTime: 60 * 60 * 1000,
        // We already retry in fetchWrapper
        retry: false,
        // This causes a stack overflow for recursive data structures
        structuralSharing: false,
        refetchOnWindowFocus: false,
        queryFn,
        queryKeyHashFn,
      },
    },
  });
}

export interface QueryCacheProviderProps {
  children?: ReactNode;
  queryClient?: RQ.QueryClient;
}
export function QueryCacheProvider({
  children,
  queryClient,
}: QueryCacheProviderProps): ReactElement {
  const clientRef = useRef<RQ.QueryClient | null>(null);
  const client = queryClient ?? (clientRef.current ??= createQueryClient());
  return createElement(
    RQ.QueryClientProvider,
    // If the client is changed, we need to force a full remount.
    // RQ doesn't properly handle switching clients dynamically,
    // and queries will start behaving incorrectly!
    { key: getObjectId(client), client },
    children,
  );
}

export interface QueryCache {
  unsafe_getQueryData<const Request extends AnyRequest>(
    queryKey: QueryKey<Request>,
  ): RequestData<Request> | undefined;

  setQueryData<const Request extends AnyRequest>(
    queryKey: QueryKey<Request>,
    updater: NoInfer<Updater<RequestData<Request>>>,
  ): RequestData<Request> | undefined;

  fetchQuery<const Request extends AnyRequest>(
    queryKey: QueryKey<Request>,
  ): Promise<RequestData<Request>>;

  refetchQuery<const Request extends AnyRequest>(
    queryKey: QueryKey<Request>,
  ): void;

  invalidateQuery<const Request extends AnyRequest>(
    queryKey: QueryKey<Request>,
  ): void;

  invalidateQueryAsync<const Request extends AnyRequest>(
    queryKey: QueryKey<Request>,
  ): Promise<void>;

  prefetchQuery<const Request extends AnyRequest>(
    queryKey: QueryKey<Request>,
  ): Promise<void>;
}

export const getQueryCache = memoizeWithWeakMap(
  (queryClient: RQ.QueryClient): QueryCache => ({
    unsafe_getQueryData(queryKey) {
      return queryClient.getQueryData(queryKey);
    },

    setQueryData(queryKey, updater) {
      return queryClient.setQueryData<any>(queryKey, updater);
    },

    async fetchQuery(queryKey) {
      try {
        return await queryClient.fetchQuery({ queryKey });
      } catch (error) {
        if (RQ.isCancelledError(error)) {
          queryKey[1].signal.throwIfAborted();
          throw createAbortError('React Query cancelled the request', error);
        }
        throw error;
      }
    },

    refetchQuery(queryKey) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      queryClient.refetchQueries({ queryKey, exact: true });
    },

    invalidateQuery(queryKey) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      queryClient.invalidateQueries({ queryKey, exact: true });
    },

    async invalidateQueryAsync(queryKey) {
      try {
        return await queryClient.invalidateQueries({ queryKey, exact: true });
      } catch (error) {
        if (RQ.isCancelledError(error)) {
          queryKey[1].signal.throwIfAborted();
          throw createAbortError('React Query cancelled the request', error);
        }
        throw error;
      }
    },

    async prefetchQuery(queryKey) {
      unsafe_asMutable(queryKey[1]).signal = DEFAULT_REQUEST_CONTEXT.signal;

      const queryOptions = {
        queryKey,
        meta: { priority: 'low' },
      } satisfies RQ.QueryOptions;

      // Make the query subscribe to itself, to the listener count never goes to zero while it's being fetched.
      const observer = new RQ.QueryObserver(queryClient, queryOptions);

      const promise = queryClient.prefetchQuery(queryOptions);

      const unsubscribe = observer.subscribe(noop);

      try {
        await promise;
      } finally {
        unsubscribe();
      }
    },
  }),
);

export default function useQueryCache(): QueryCache {
  const queryClient = RQ.useQueryClient();
  return getQueryCache(queryClient);
}
